/** external */
import _ from "lodash";
import React, { useState } from "react";
import PropTypes from "prop-types";
import { IconButton, Tooltip, Dialog } from "@toddle-edu/ds-web";
import { colors, fontStyles } from "@toddle-edu/ds-theme";
import { DeleteOutlined } from "@toddle-edu/ds-icons";
import classNames from "classnames";

/** internal */
import { fontStyle } from "Constants";
import { FieldComponent } from "UnitPlans/components/IBcomponents";
import { useI18n } from "Hooks";
import { getStyleStrippedText } from "Utils";
import { onEnterOrSpaceKeyDown } from "Utils/AccessibilityHelpers";
import {
  MODE_TEACHER_CREATE,
  MODE_TEACHER_EVALUATE,
  MODE_TEACHER_VIEW,
  MODE_ASSESSMENT_VIEW,
} from "Worksheet/modules/WorksheetConstants";
import { isStudentEndMode } from "Worksheet/modules/WorksheetUtils";
import { getIsWorksheetDocEditorEnabled } from "Worksheet/modules/WorksheetFeatureFlags";
/** relative */
import classes from "./TextBox.scss";
import ReorderButtons from "../SubQuestions/ReorderButtons";

const TextBox = props => {
  const {
    id,
    uid,
    value,
    updateFields,
    deleteQuestion,
    questionContainerRef,
    isCreateMode,
    fieldMode,
    mode,
    isSubQuestion,
    customRef,
    isActiveQuestion,
    updateActiveQuestion,
    questionId,
    showErrorState,
    isFromLibrary,
    disableTextBox,
    isGeneratingAIQuestions,
    onMoveUp,
    onMoveDown,
    isFirstInstruction,
    isLastInstruction,
    isFromInternalTool,
    height,
  } = props;

  // Student end revamp: use flag
  const isStudentRevamp = isStudentEndMode(mode);
  const showDocEditor = getIsWorksheetDocEditorEnabled();

  const { t } = useI18n(["common", "worksheet"]);

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const toggleDeleteDialog = e => {
    e?.stopPropagation?.();
    setShowDeleteDialog(!showDeleteDialog);
  };

  const onDeleteClick = () => {
    deleteQuestion();
    toggleDeleteDialog();
  };

  const onFocusInputField = () => {
    if (!isActiveQuestion) {
      updateActiveQuestion(questionId);
    }
  };

  const onRichTextClick = e => {
    e?.stopPropagation?.();
    if (isCreateMode && fieldMode == "view") onFocusInputField();
  };

  const updateInputField = async ({ params }) => {
    const fields = [
      {
        id: id,
        value: params[uid],
      },
    ];

    updateFields({ fields, isDebounce: true });
  };

  const styles = {
    customTextStyle: {
      ...fontStyle.medium,
      fontSize: "1.4rem",
      lineHeight: 1.71,
      fontWeight: isStudentRevamp ? "500" : "400",
    },
    textStyle: {
      fontSize: "16px",
      color: colors.textDefault,
      ...fontStyles.medium,
      fontWeight: "400",
    },
    singlePageFlowCustomTextStyle: {
      fontSize: "16px",
      color: "var(--text-primary)",
      ...fontStyles.medium,
    },
  };
  const containerClasses = classNames({
    [classes.container]: mode == MODE_TEACHER_CREATE,
    [classes.textBox]: !isCreateMode,
    [classes.textBoxAssessmentMode]:
      mode == MODE_ASSESSMENT_VIEW && isFromLibrary,
    [classes.marginContainer]:
      !isSubQuestion &&
      !isFromLibrary &&
      !(isCreateMode || isGeneratingAIQuestions),
    [classes.marginContainerImportFromLibrary]: !isSubQuestion && isFromLibrary,
    [classes.contentBox]: _.includes(
      [MODE_TEACHER_EVALUATE, MODE_TEACHER_VIEW],
      mode
    ),
    [classes.richTextCondensed]: !isActiveQuestion,
    [classes.richTextCondensedV2]:
      isCreateMode && !isActiveQuestion && !isFromLibrary,
    [classes.richTextCondensedHover]:
      !showErrorState && !isActiveQuestion && isCreateMode,
    [classes.errorContainer]: showErrorState,
    [classes.disableTextBoxContainer]: disableTextBox,
    [classes.studentRevampClasses]: isStudentRevamp,
  });

  const deleteIconClasses = classNames({
    [classes.deleteIcon]: !showDocEditor,
    [classes.deleteIconExtraGap]: isFromInternalTool,
    [classes.deleteIconCondensed]: !isActiveQuestion && !showDocEditor,
  });
  const richTextViewModeClasses = classNames({
    [classes.richTextViewMode]: true,
    [classes.paddingRight]: mode == MODE_TEACHER_CREATE && fieldMode == "view",
  });

  const fieldValue = _.isEqual(fieldMode, "view")
    ? getStyleStrippedText({
        text: value,
        type: "DOCUMENT_EDITOR",
      })
    : value;

  return (
    <>
      <div
        className={containerClasses}
        ref={questionContainerRef}
        id={
          isActiveQuestion ? "active-question-card" : "active-question-toggle"
        }
        onClick={onRichTextClick}
        onKeyDown={e => onEnterOrSpaceKeyDown(e, onRichTextClick)}
        role="button"
        tabIndex={0}
        data-test-id="worksheet-questionType-textBox"
      >
        {mode == MODE_TEACHER_CREATE && isActiveQuestion && !showDocEditor ? (
          <div className={deleteIconClasses}>
            <Tooltip
              tooltip={t("common:delete_with_label", {
                label: t(
                  `worksheet:${
                    isCreateMode && !isSubQuestion
                      ? "inline_text"
                      : "content_box"
                  }`
                ),
              })}
            >
              <IconButton
                onClick={toggleDeleteDialog}
                dsVersion="2.0"
                variant="neutral"
                type="plain"
                size="small"
                _legacy_variant="plain"
                icon={<DeleteOutlined />}
                _legacy_size={"x-small"}
                testId="worksheet-textBox-toggleDeleteDialog"
              />
            </Tooltip>
            {/* reorder instructions */}
            {isFromInternalTool ? (
              <ReorderButtons
                onMoveUp={onMoveUp}
                onMoveDown={onMoveDown}
                isFirstQuestion={isFirstInstruction}
                isLastQuestion={isLastInstruction}
              />
            ) : null}
          </div>
        ) : null}
        <div className={richTextViewModeClasses}>
          <FieldComponent
            type={"RichText_Ported"}
            value={fieldValue}
            updateInputField={updateInputField}
            onFocusInputField={onFocusInputField}
            customTextStyle={
              !isSubQuestion
                ? styles.singlePageFlowCustomTextStyle
                : styles.customTextStyle
            }
            fieldId={id}
            fieldKey={uid}
            key={uid}
            mode={fieldMode}
            isRequired={true}
            customRef={customRef}
            emptyText={t("common:add_with_label", {
              label: t("common:instruction"),
            })}
            textStyle={styles.textStyle}
            renderNewEditor={true}
            enableEditorObserver={true}
            shouldCallOnBlur={false}
            hideErrorMessage={true}
            isRtlAlignmentRequired={true}
            isActive={isCreateMode}
            showMediaLinkInTheatreMode={true}
            {...(mode === MODE_TEACHER_CREATE
              ? { toolbarPosition: "bottom" }
              : {})}
            height={height}
            showDocEditor={showDocEditor}
            extendedToolbarButtons={
              <div className={deleteIconClasses}>
                <Tooltip
                  tooltip={t("common:delete_with_label", {
                    label: t(
                      `worksheet:${
                        isCreateMode && !isSubQuestion
                          ? "inline_text"
                          : "content_box"
                      }`
                    ),
                  })}
                >
                  <IconButton
                    onClick={toggleDeleteDialog}
                    dsVersion="2.0"
                    variant="neutral"
                    type="plain"
                    size="small"
                    _legacy_variant="plain"
                    icon={<DeleteOutlined />}
                    _legacy_size={"x-small"}
                    testId="worksheet-textBox-toggleDeleteDialog"
                  />
                </Tooltip>
                {/* reorder instructions */}
                {isFromInternalTool ? (
                  <ReorderButtons
                    onMoveUp={onMoveUp}
                    onMoveDown={onMoveDown}
                    isFirstQuestion={isFirstInstruction}
                    isLastQuestion={isLastInstruction}
                  />
                ) : null}
              </div>
            }
          />
        </div>
      </div>
      {showDeleteDialog ? (
        <Dialog
          variant="delete-dialog"
          title={t("common:delete_with_label", {
            label: t(
              !isSubQuestion
                ? "worksheet:inline_text"
                : "common:instruction_plural"
            ),
          })}
          message={t("common:delete_msg_with_this_label_lowercase", {
            label: t(
              !isSubQuestion
                ? "worksheet:inline_text"
                : "common:instruction_plural"
            ),
          })}
          buttonOneText={t("common:cancel")}
          buttonTwoText={t("common:confirm")}
          onButtonTwoClick={onDeleteClick}
          onButtonOneClick={toggleDeleteDialog}
          onClose={toggleDeleteDialog}
          testId="worksheet-textBox-deleteDialog"
        />
      ) : null}
    </>
  );
};

TextBox.propTypes = {
  id: PropTypes.string,
  uid: PropTypes.string,
  value: PropTypes.string,
  updateFields: PropTypes.func,
  deleteQuestion: PropTypes.func,
  questionContainerRef: PropTypes.any,
  isSubQuestion: PropTypes.bool,
  customRef: PropTypes.func,
  isActiveQuestion: PropTypes.bool,
  updateActiveQuestion: PropTypes.func,
  questionId: PropTypes.string,
  isFromLibrary: PropTypes.bool,
  disableTextBox: PropTypes.bool,
  isGeneratingAIQuestions: PropTypes.bool,
  onMoveUp: PropTypes.func,
  onMoveDown: PropTypes.func,
  isFirstInstruction: PropTypes.bool,
  isLastInstruction: PropTypes.bool,
  isFromInternalTool: PropTypes.bool,
};

TextBox.defaultProps = {
  disableTextBox: false,
  isGeneratingAIQuestions: false,
  onMoveUp: _.noop,
  onMoveDown: _.noop,
  isFirstInstruction: false,
  isLastInstruction: false,
  isFromInternalTool: false,
};

export default TextBox;
